/** Channel aggregation for the programs table and ranking assets. */

export interface Top10AggregatePodcast {
    _id: string;
    title: string;
    /** Channel cover (preferred over program image when aggregating by podcast). */
    imageS3Key?: string;
    imageUrl?: string;
    imageUpdatedAt?: string | null;
    updatedAt?: string | null;
    socialMetrics?: {
        youtube?: { subscriberCount?: number };
        instagram?: { followerCount?: number };
        tiktok?: { followerCount?: number };
    };
}

/** Program shape required for full-metric channel aggregation (main table). */
export interface ChannelAnalysisProgramInput {
    _id: string;
    name: string;
    imageS3Key?: string;
    imageUpdatedAt?: string;
    updatedAt?: string;
    podcast: Top10AggregatePodcast & {
        socialAccounts?: { instagram?: string; tiktok?: string };
    };
    contentCount: number;
    totalViews: number;
    totalViewsLongForm?: number;
    totalViewsDay7?: number;
    totalLikes: number;
    totalComments: number;
    totalInteractions: number;
    totalInteractionsDay7?: number;
    avgViews: number;
    avgViewsDay7: number;
    avgViewsAllTime: number;
    avgInteractions: number;
    avgInteractionsDay7: number;
    engagementRate: number;
    uniquePaidBrandsCount: number;
    totalDurationMinutes?: number;
    dateRange: { earliest: string; latest: string };
    rankingPosition?: number;
    positionLastWeek?: number | null;
    avgViewsLastWeek?: number | null;
}

export interface ChannelAnalysisData {
    _id: string;
    name: string;
    podcast: ChannelAnalysisProgramInput['podcast'];
    contentCount: number;
    totalViews: number;
    totalViewsLongForm: number;
    totalViewsDay7: number;
    totalLikes: number;
    totalComments: number;
    totalInteractions: number;
    totalInteractionsDay7: number;
    avgViews: number;
    avgViewsDay7: number;
    avgViewsAllTime: number;
    avgInteractions: number;
    avgInteractionsDay7: number;
    engagementRate: number;
    uniquePaidBrandsCount: number;
    totalDurationMinutes: number;
    dateRange: { earliest: string; latest: string };
    programCount: number;
    programs: ChannelAnalysisProgramInput[];
    rankingPosition?: number;
    positionLastWeek?: number | null;
    avgViewsLastWeek?: number | null;
}

function weightedAvg(group: ChannelAnalysisProgramInput[], field: keyof ChannelAnalysisProgramInput): number {
    const contentCount = group.reduce((s, p) => s + p.contentCount, 0);
    if (contentCount <= 0) return 0;
    const numerator = group.reduce((s, p) => s + (p[field] as number) * p.contentCount, 0);
    return numerator / contentCount;
}

function buildChannelRowFromGroup(group: ChannelAnalysisProgramInput[]): ChannelAnalysisData {
    const contentCount = group.reduce((s, p) => s + p.contentCount, 0);
    const totalViews = group.reduce((s, p) => s + p.totalViews, 0);
    const totalViewsLongForm = group.reduce((s, p) => s + (p.totalViewsLongForm ?? 0), 0);
    const totalViewsDay7 = group.reduce((s, p) => s + (p.totalViewsDay7 ?? 0), 0);
    const totalLikes = group.reduce((s, p) => s + p.totalLikes, 0);
    const totalComments = group.reduce((s, p) => s + p.totalComments, 0);
    const totalInteractions = group.reduce((s, p) => s + p.totalInteractions, 0);
    const totalInteractionsDay7 = group.reduce((s, p) => s + (p.totalInteractionsDay7 ?? 0), 0);
    const totalDurationMinutes = group.reduce((s, p) => s + (p.totalDurationMinutes ?? 0), 0);
    const uniquePaidBrandsCount = group.reduce((s, p) => s + p.uniquePaidBrandsCount, 0);

    const avgViews = contentCount > 0 ? totalViews / contentCount : 0;
    const avgViewsDay7 = weightedAvg(group, 'avgViewsDay7');
    const avgViewsAllTime = weightedAvg(group, 'avgViewsAllTime');
    const avgInteractions = contentCount > 0 ? totalInteractions / contentCount : 0;
    const avgInteractionsDay7 = weightedAvg(group, 'avgInteractionsDay7');
    const engagementRate = totalViews > 0 ? ((totalLikes + totalComments) / totalViews) * 100 : 0;

    const earliest = group.reduce(
        (min, p) => (p.dateRange.earliest && (!min || p.dateRange.earliest < min) ? p.dateRange.earliest : min),
        '' as string,
    );
    const latest = group.reduce(
        (max, p) => (p.dateRange.latest && (!max || p.dateRange.latest > max) ? p.dateRange.latest : max),
        '' as string,
    );

    const rep = group.reduce(
        (best, p) => (p.totalViews > best.totalViews ? p : best),
        group[0]!,
    );

    const prevWeekPrograms = group.filter((p) => p.avgViewsLastWeek != null);
    const prevWeekContentCount = prevWeekPrograms.reduce((s, p) => s + p.contentCount, 0);
    const avgViewsLastWeek =
        prevWeekContentCount > 0
            ? prevWeekPrograms.reduce((s, p) => s + (p.avgViewsLastWeek ?? 0) * p.contentCount, 0) / prevWeekContentCount
            : null;

    return {
        _id: rep.podcast._id,
        name: rep.podcast.title,
        podcast: rep.podcast,
        contentCount,
        totalViews,
        totalViewsLongForm,
        totalViewsDay7,
        totalLikes,
        totalComments,
        totalInteractions,
        totalInteractionsDay7,
        avgViews,
        avgViewsDay7,
        avgViewsAllTime,
        avgInteractions,
        avgInteractionsDay7,
        engagementRate,
        uniquePaidBrandsCount,
        totalDurationMinutes,
        dateRange: { earliest, latest },
        programCount: group.length,
        programs: group,
        avgViewsLastWeek,
    };
}

/** Roll up program analysis rows to one row per podcast (channel) with full metrics. */
export function aggregateProgramsToChannelAnalysisRows(
    programs: ChannelAnalysisProgramInput[],
): ChannelAnalysisData[] {
    const byPodcast = new Map<string, ChannelAnalysisProgramInput[]>();
    for (const p of programs) {
        const id = p.podcast._id;
        const arr = byPodcast.get(id) ?? [];
        arr.push(p);
        byPodcast.set(id, arr);
    }

    const rows = Array.from(byPodcast.values()).map(buildChannelRowFromGroup);

    const prevWeekSorted = [...rows]
        .filter((c) => c.avgViewsLastWeek != null)
        .sort((a, b) => (b.avgViewsLastWeek ?? 0) - (a.avgViewsLastWeek ?? 0));

    const positionMap = new Map<string, number>();
    prevWeekSorted.forEach((c, i) => positionMap.set(c._id, i + 1));

    return rows.map((row) => ({
        ...row,
        positionLastWeek: positionMap.get(row._id) ?? null,
    }));
}

export function sortChannelRows(
    rows: ChannelAnalysisData[],
    orderBy: string,
    order: 'asc' | 'desc',
): ChannelAnalysisData[] {
    const dir = order === 'asc' ? 1 : -1;

    const getSortValue = (row: ChannelAnalysisData): string | number => {
        switch (orderBy) {
            case 'name':
                return row.name.toLowerCase();
            case 'podcast':
                return row.programCount;
            case 'programCount':
                return row.programCount;
            default: {
                const val = row[orderBy as keyof ChannelAnalysisData];
                return typeof val === 'number' ? val : 0;
            }
        }
    };

    return [...rows].sort((a, b) => {
        const aVal = getSortValue(a);
        const bVal = getSortValue(b);
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            const cmp = aVal.localeCompare(bVal);
            if (cmp !== 0) return cmp * dir;
        } else {
            const diff = (Number(aVal) - Number(bVal)) * dir;
            if (diff !== 0) return diff;
        }
        return b.totalViews - a.totalViews;
    });
}

/** Unique podcast/channel count from programs analysis rows (digital universe when type=digital). */
export function countChannelsFromPrograms(
  programs: ChannelAnalysisProgramInput[],
): number {
  return aggregateProgramsToChannelAnalysisRows(programs).length;
}

/** Top channels by summed 7-day views (YouTube long-form), for weekly ranking assets. */
export function buildChannelsViewsRankingRows(
    programs: ChannelAnalysisProgramInput[],
    limit = 10,
) {
    return aggregateProgramsToChannelAnalysisRows(programs)
        .filter((c) => (c.totalViewsDay7 ?? 0) > 0)
        .sort((a, b) => (b.totalViewsDay7 ?? 0) - (a.totalViewsDay7 ?? 0))
        .slice(0, limit)
        .map((c) => ({
            _id: c._id,
            name: c.name,
            imageS3Key: c.podcast.imageS3Key,
            imageUrl: c.podcast.imageUrl,
            imageUpdatedAt: c.podcast.imageUpdatedAt ?? undefined,
            updatedAt: c.podcast.updatedAt ?? undefined,
            totalViews: c.totalViewsDay7 ?? 0,
            contentCount: c.contentCount,
        }));
}

/** Top channels by summed 7-day interactions (likes+comments), for weekly ranking assets. */
export function buildChannelsInteractionsRankingRows(
    programs: ChannelAnalysisProgramInput[],
    limit = 10,
) {
    return aggregateProgramsToChannelAnalysisRows(programs)
        .filter((c) => (c.totalInteractionsDay7 ?? 0) > 0)
        .sort((a, b) => (b.totalInteractionsDay7 ?? 0) - (a.totalInteractionsDay7 ?? 0))
        .slice(0, limit)
        .map((c) => ({
            _id: c._id,
            name: c.name,
            imageS3Key: c.podcast.imageS3Key,
            imageUrl: c.podcast.imageUrl,
            imageUpdatedAt: c.podcast.imageUpdatedAt ?? undefined,
            updatedAt: c.podcast.updatedAt ?? undefined,
            /** Reuses ChannelRow.totalViews slot for the displayed metric (same as channels-views). */
            totalViews: c.totalInteractionsDay7 ?? 0,
            contentCount: c.contentCount,
        }));
}
